"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { composerAttestation, concerne, valider } from "@/lib/tools/spouseInformation";
import { dateEnToutesLettres, documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Question,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * L'attestation d'information au conjoint commun en biens.
 *
 * Le parcours commence par un TRI, pas par une saisie : ce document n'est
 * demandé qu'à un entrepreneur marié sous un régime de communauté. Un pacsé,
 * un célibataire et un époux séparé de biens repartent en deux écrans avec la
 * raison, plutôt qu'avec un document qui ne leur servirait à rien. C'est la
 * moitié de l'utilité de l'outil : la plupart des gens qui le cherchent ne le
 * doivent pas.
 *
 * La confusion à écarter, et la seule que ce parcours traite en toutes
 * lettres : il existe un SECOND document autour du conjoint, l'attestation par
 * laquelle celui qui travaille dans l'entreprise choisit son statut. Elle est
 * signée par le conjoint, pas par l'entrepreneur, et elle vise aussi les
 * pacsés et les concubins. Les deux se ressemblent de loin et ne se
 * remplacent pas.
 *
 * Comme le premier formulaire : rien ne sort du navigateur, la signature vit
 * dans le stockage de session, et le document reste en français.
 */

const L = {
    fr: {
        phases: ["Votre situation", "Les identités", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : le document s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        situationTitre: "Vivez-vous en couple, et sous quelle forme ?",
        situationAide: "Ce document ne vise que le mariage. Deux écrans suffisent à savoir s'il vous est demandé.",
        marie: "Je suis marié",
        marieDetail: "Quelle que soit la date du mariage",
        pacse: "Je suis pacsé",
        pacseDetail: "Pacs, pas mariage",
        seul: "Aucun des deux",
        seulDetail: "Célibataire, en concubinage, veuf ou divorcé",
        regimeTitre: "Sous quel régime matrimonial ?",
        regimeAide: "C'est votre contrat de mariage qui répond, ou son absence. En cas de doute, l'acte de mariage le mentionne.",
        sansContrat: "Sans contrat de mariage",
        sansContratDetail: "Le cas le plus courant",
        contrat: "Avec un contrat prévoyant des biens communs",
        contratDetail: "Communauté aménagée ou universelle",
        separation: "Séparation de biens",
        separationDetail: "Ou participation aux acquêts",
        horsSujetTitre: "Ce document ne vous est pas demandé",
        motifPacse: "L'attestation ne vise que les époux mariés sous un régime de communauté. Un Pacs ne crée pas de biens communs au sens de l'article R. 123-121-1, et le guichet ne vous réclamera donc pas cette pièce.",
        motifSeul: "L'attestation atteste que vous avez informé votre conjoint. Sans conjoint marié, il n'y a personne à informer, et rien à joindre à votre dossier.",
        motifSeparation: "Sous séparation de biens, vos dettes professionnelles n'engagent pas de biens communs, puisqu'il n'y en a pas. L'attestation ne concerne que les régimes qui en comportent.",
        autreDocumentTitre: "Attention à ne pas confondre",
        autreDocument: "Si votre conjoint, votre partenaire de Pacs ou votre concubin travaille régulièrement avec vous, un autre document existe : l'attestation par laquelle il choisit son statut, collaborateur, salarié ou associé. C'est lui qui la signe.",
        identiteTitre: "Qui êtes-vous ?",
        identiteAide: "C'est vous qui signez : l'attestation dit que vous avez informé votre conjoint, pas l'inverse.",
        madame: "Madame",
        civilite: "Votre civilité",
        civiliteConjoint: "Civilité de votre conjoint",
        monsieur: "Monsieur",
        champPrenoms: "Vos prénoms",
        champNom: "Votre nom",
        aideNom: "Votre nom de naissance, tel qu'il figure sur votre acte de mariage.",
        conjointTitre: "Et votre conjoint ?",
        conjointAide: "Son nom apparaîtra dans l'attestation. C'est la personne que vous déclarez avoir informée.",
        champConjointPrenoms: "Ses prénoms",
        champConjointNom: "Son nom",
        signatureTitre: "Où signez-vous, et quand ?",
        signatureAide: "Signez à la date de votre dépôt ou juste avant : une attestation postérieure au dossier fait tiquer.",
        champLieuSignature: "Lieu de signature",
        champDateSignature: "Date de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",
        continuer: "Continuer",
        voirDocument: "Voir mon document",
        resultatTitre: "Votre attestation",
        apercu: "Aperçu du document",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",
        erreurDateLue: "Nous lisons {date}. Vérifiez l'année.",
        erreurFormat: "Ce format n'est pas reconnu.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais leur contenu ne passe pas. Corrigez-les et revenez ici.",
        motifFormat: "le format n'est pas reconnu",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre document.",
        suiteTitre: "Et maintenant",
        suite1: "Imprimez le document, signez-le à la main si vous ne l'avez pas signé ici, puis scannez-le ou photographiez-le.",
        suite2: "Joignez-le à votre formalité de création sur le guichet unique, avec vos autres justificatifs.",
        suite3: "Gardez-en une copie : le greffe peut vous la redemander pendant l'instruction de votre dossier.",
        fondTitre: "Ce que vous attestez vraiment",
        fond: "Le document atteste que vous avez déjà prévenu votre conjoint. Avant de le signer, expliquez-lui donc que les dettes nées de votre activité pourront être payées sur vos biens communs, l'épargne du ménage et le logement acheté ensemble compris. Ses propres salaires, eux, restent hors d'atteinte de vos créanciers. Comptez dix minutes de conversation.",
        docEnFrancais: "",
    },
    en: {
        phases: ["Your situation", "The two names", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device: the document is built there, nobody receives your answers, not even us, and it all disappears with the tab.",
        situationTitre: "Are you in a couple, and under what form?",
        situationAide: "This document only concerns marriage. Two screens are enough to know whether it is asked of you.",
        marie: "I am married",
        marieDetail: "Whatever the date of the marriage",
        pacse: "I am in a civil partnership",
        pacseDetail: "A French Pacs, not a marriage",
        seul: "Neither of those",
        seulDetail: "Single, cohabiting, widowed or divorced",
        regimeTitre: "Under which matrimonial regime?",
        regimeAide: "Your marriage contract answers this, or its absence. If in doubt, your marriage certificate says so.",
        sansContrat: "No marriage contract",
        sansContratDetail: "The most common case",
        contrat: "A contract with community property",
        contratDetail: "Adjusted or universal community",
        separation: "Separation of property",
        separationDetail: "Or participation in acquired assets",
        horsSujetTitre: "This document is not asked of you",
        motifPacse: "The attestation only concerns spouses married under a community regime. A French civil partnership creates no community property within the meaning of article R. 123-121-1, so the single window will not ask you for it.",
        motifSeul: "The attestation states that you have informed your spouse. With no spouse, there is nobody to inform, and nothing to attach to your file.",
        motifSeparation: "Under separation of property, your business debts commit no community property, because there is none. The attestation only concerns regimes that have some.",
        autreDocumentTitre: "One document not to confuse it with",
        autreDocument: "If your spouse, civil partner or cohabiting partner works with you regularly, another document exists: the one in which they choose their status, collaborator, employee or associate. They sign it themselves.",
        identiteTitre: "Who are you?",
        identiteAide: "You are the one signing: the attestation says that you informed your spouse, not the other way round.",
        madame: "Ms",
        civilite: "Your title",
        civiliteConjoint: "Your spouse's title",
        monsieur: "Mr",
        champPrenoms: "Your forenames",
        champNom: "Your surname",
        aideNom: "Your birth name, as it appears on your marriage certificate.",
        conjointTitre: "And your spouse?",
        conjointAide: "Their name will appear in the attestation. They are the person you declare having informed.",
        champConjointPrenoms: "Their forenames",
        champConjointNom: "Their surname",
        signatureTitre: "Where are you signing, and when?",
        signatureAide: "Sign on the day you file, or just before: an attestation dated after the application raises questions.",
        champLieuSignature: "Place of signature",
        champDateSignature: "Date of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",
        continuer: "Continue",
        voirDocument: "See my document",
        resultatTitre: "Your attestation",
        apercu: "Document preview",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",
        erreurDateLue: "We read {date}. Please check the year.",
        erreurFormat: "This format is not recognised.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but their content is not accepted. Correct them and come back.",
        motifFormat: "the format is not recognised",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your document.",
        suiteTitre: "What happens next",
        suite1: "Print the document, sign it by hand if you did not sign it here, then scan or photograph it.",
        suite2: "Attach it to your registration formality on the French single window, with your other supporting documents.",
        suite3: "Keep a copy: the registry may ask for it again while your file is being processed.",
        fondTitre: "What you are actually attesting",
        fond: "The document states that you have already told your spouse. So before signing it, explain to them that debts arising from your business can be paid out of your community property, including the household savings and the home you bought together. Their own salary, however, stays out of your creditors' reach. Allow ten minutes for the conversation.",
        docEnFrancais: "The document below stays in French: it is filed with a French authority, and its wording is fixed by ministerial order.",
    },
};

const PHASES_PAR_ECRAN = {
    situation: 0,
    regime: 0,
    horsSujet: 0,
    identite: 1,
    conjoint: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    situation: "",
    regime: "",
    civilite: "",
    prenoms: "",
    nom: "",
    conjointCivilite: "",
    conjointPrenoms: "",
    conjointNom: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

/** Les libellés des champs manquants, hors du dictionnaire lu par le contrôle
 *  de parité : ce sont des clés de la lib, pas des libellés du JSX. */
const LIBELLES_MANQUES = {
    fr: {
        civilite: "Votre civilité",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        conjointCivilite: "La civilité de votre conjoint",
        conjointPrenoms: "Les prénoms de votre conjoint",
        conjointNom: "Le nom de votre conjoint",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de signature",
        situation: "Votre situation",
    },
    en: {
        civilite: "Your title",
        prenoms: "Your forenames",
        nom: "Your surname",
        conjointCivilite: "Your spouse's title",
        conjointPrenoms: "Your spouse's forenames",
        conjointNom: "Your spouse's surname",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of signature",
        situation: "Your situation",
    },
};

export default function AttestationInformationConjoint({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    // L'identité gardée sur l'appareil préremplit l'écran « identité », que
    // le lecteur relit : rien n'est sauté.
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [copie, setCopie] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "situation",
        () => {
            // L'identité gardée revient : on recommence le document, pas sa saisie.
            setR({ ...VIDE, ...identite });
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    // Seule l'identité du déclarant est proposée à la garde, jamais celle du
    // conjoint, une fois son écran passé.
    useSignalerIdentite(
        { civilite: r.civilite, prenoms: r.prenoms, nom: r.nom },
        etape === "conjoint" || etape === "signature" || etape === "resultat",
    );

    // La date du jour vient de la page, jamais d'un new Date() ici : lue des
    // deux côtés, elle produirait un écart d'hydratation.
    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses), [reponses]);
    const declaration = useMemo(
        () => composerAttestation(reponses, aujourdhui),
        [reponses, aujourdhui],
    );
    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());

    const motifLisible = () => t.motifFormat;


    /* Sous le champ, le refus se dit en phrase, et surtout il MONTRE la date
       comprise : « nous lisons 3 mai 0019 » est plus parlant que « année
       invalide » devant un champ où l'on croit avoir tapé 1962. */
    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (refus.motif === "ageImpossible") {
            return remplir(t.erreurDateLue, { date: dateEnToutesLettres(r[champ]) });
        }
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };

    const telecharger = () =>
        telechargerDocument({
            document: declaration,
            signature,
            nomFichier: nomDuFichier("attestation-information-conjoint", reponses),
        });

    const copier = async () => {
        if (!declaration) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(declaration));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé par le navigateur : le bouton ne ment pas,
            // il ne se passe rien et le PDF reste la voie normale.
        }
    };

    /** Le motif du refus, quand le parcours s'arrête avant la saisie. */
    const motif = concerne(r).motif;

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "situation") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.situationTitre} aide={t.situationAide}>
                {/* Les cartes de Journey, pas une copie locale : le format de la
                    rubrique vit à un seul endroit. L'écran garde sa forme
                    d'`Ecran` pour pouvoir porter la promesse en pied. */}
                <div className="grid sm:grid-cols-3 gap-3">
                    {[
                        { cle: "marie", icone: "users-group-rounded-linear", titre: t.marie, detail: t.marieDetail },
                        { cle: "pacse", icone: "users-group-two-rounded-linear", titre: t.pacse, detail: t.pacseDetail },
                        { cle: "seul", icone: "user-rounded-linear", titre: t.seul, detail: t.seulDetail },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, situation: c.cle }));
                                aller(c.cle === "marie" ? "regime" : "horsSujet");
                            }}
                        />
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "regime") {
        return (
            <Question
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.regimeTitre}
                aide={t.regimeAide}
                choix={[
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.sansContrat,
                        detail: t.sansContratDetail,
                        onClick: () => {
                            setR((p) => ({ ...p, regime: "sansContrat" }));
                            aller("identite");
                        },
                    },
                    {
                        icone: "clipboard-check-linear",
                        titre: t.contrat,
                        detail: t.contratDetail,
                        onClick: () => {
                            setR((p) => ({ ...p, regime: "contratAvecBiensCommuns" }));
                            aller("identite");
                        },
                    },
                    {
                        icone: "shield-linear",
                        titre: t.separation,
                        detail: t.separationDetail,
                        onClick: () => {
                            setR((p) => ({ ...p, regime: "separation" }));
                            aller("horsSujet");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {motif === "pacse" && t.motifPacse}
                        {motif === "seul" && t.motifSeul}
                        {motif === "separation" && t.motifSeparation}
                    </p>

                    <div className="mt-6 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="info-circle-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">
                                {t.autreDocumentTitre}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {t.autreDocument}
                        </p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "identite") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.identiteTitre}
                aide={t.identiteAide}
            >
                <div className="space-y-5">
                    <Segments
                        libelle={t.civilite}
                        options={[
                            { cle: "madame", libelle: t.madame },
                            { cle: "monsieur", libelle: t.monsieur },
                        ]}
                        valeur={r.civilite}
                        poser={poser("civilite")}
                    />
                    <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                    <Champ
                        libelle={t.champNom}
                        aide={t.aideNom}
                        valeur={r.nom}
                        poser={poser("nom")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("civilite", "prenoms", "nom")}
                    onClick={() => aller("conjoint")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "conjoint") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.conjointTitre}
                aide={t.conjointAide}
            >
                <div className="space-y-5">
                    <Segments
                        libelle={t.civiliteConjoint}
                        options={[
                            { cle: "madame", libelle: t.madame },
                            { cle: "monsieur", libelle: t.monsieur },
                        ]}
                        valeur={r.conjointCivilite}
                        poser={poser("conjointCivilite")}
                    />
                    <Champ
                        libelle={t.champConjointPrenoms}
                        valeur={r.conjointPrenoms}
                        poser={poser("conjointPrenoms")}
                    />
                    <Champ
                        libelle={t.champConjointNom}
                        valeur={r.conjointNom}
                        poser={poser("conjointNom")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("conjointCivilite", "conjointPrenoms", "conjointNom")}
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={2}
                onRetour={retour}
                titre={t.signatureTitre}
                aide={t.signatureAide}
            >
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("lieuDeSignature")}
                    onClick={() => aller("resultat")}
                >
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!declaration ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li
                                key={cle}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {motifLisible(motif)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    {t.docEnFrancais && (
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-3">
                            {t.docEnFrancais}
                        </p>
                    )}

                    <ApercuDocument document={declaration} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon
                                name={copie ? "check-circle-linear" : "copy-linear"}
                                className="text-lg"
                            />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {t.suiteTitre}
                            </div>
                        </div>
                        <ul className="space-y-3">
                            {[t.suite1, t.suite2, t.suite3].map((ligne) => (
                                <li
                                    key={ligne}
                                    className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                                >
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="users-group-rounded-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">
                                {t.fondTitre}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {t.fond}
                        </p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
